import {
  Maximize2Icon,
  MicIcon,
  MicOffIcon,
  Minimize2Icon,
  MonitorUpIcon,
  PhoneIcon,
  PhoneOffIcon,
  VideoIcon,
  VideoOffIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useCurrentUser } from '@/shared/auth';
import { Avatar } from '@/shared/components/avatar';
import { cn } from '@/shared/lib/cn';
import { layerClass } from '@/shared/ui/layers';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { formatDuration } from '../lib/format';
import type { Call } from '../types';

import { callManager } from './callManager';
import { useCallStore, type RemotePeer } from './callStore';
import { startRingtone } from './ringtone';

/** Incoming call prompts and the window of the current call; mounted once for the whole app. */
export function CallLayer() {
  const incoming = useCallStore((s) => s.incoming);
  const active = useCallStore((s) => s.status !== 'idle');
  useEffect(() => {
    const onPageHide = () => callManager.leaveOnUnload();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);
  return createPortal(
    <>
      {active && <CallWindow />}
      {incoming[0] && <IncomingCall key={incoming[0].id} call={incoming[0]} />}
    </>,
    document.body,
  );
}

/** Screen sharing needs `getDisplayMedia` (desktop browsers, secure origins). */
const canShareScreen = () =>
  typeof (navigator.mediaDevices as MediaDevices | undefined)?.getDisplayMedia === 'function';

// ---- incoming ---------------------------------------------------------------------------------------------------

function IncomingCall({ call }: { call: Call }) {
  const { t } = useTranslation('messenger');
  const caller = call.initiator;
  const group = call.chatType === 'group';

  useEffect(() => startRingtone(), []);

  return (
    <div
      role="alertdialog"
      aria-modal="false"
      aria-labelledby="incoming-call-title"
      className={cn(
        'dark fixed inset-x-3 top-3 mx-auto w-auto max-w-sm overflow-hidden rounded-2xl border bg-popover/95 p-4 text-foreground shadow-2xl backdrop-blur sm:inset-x-auto sm:top-16 sm:right-5 sm:w-96',
        layerClass.modal,
        'animate-in fade-in slide-in-from-top-4',
      )}
      data-testid="incoming-call"
      data-call-id={call.id}
    >
      <div className="flex items-center gap-3">
        <span className="relative flex">
          <span className="absolute inset-0 animate-ping rounded-full bg-success/40" aria-hidden />
          {caller && <Avatar user={caller} size="xl" className="relative" />}
        </span>
        <div className="min-w-0 flex-1">
          <p id="incoming-call-title" className="truncate text-base font-semibold">
            {caller?.fullName}
          </p>
          <p className="truncate text-sm text-muted-foreground">
            {group
              ? t(call.video ? 'call.incomingVideoGroup' : 'call.incomingAudioGroup', {
                  title: call.chatTitle,
                })
              : t(call.video ? 'call.incomingVideo' : 'call.incomingAudio')}
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <RoundButton
          label={t('call.decline')}
          tone="danger"
          onClick={() => void callManager.decline(call)}
          testId="decline-call"
        >
          <PhoneOffIcon />
        </RoundButton>
        <RoundButton
          label={t('call.acceptAudio')}
          tone="success"
          onClick={() => void callManager.accept(call, false)}
          testId="accept-call"
        >
          <PhoneIcon />
        </RoundButton>
        <RoundButton
          label={t('call.acceptVideo')}
          tone="success"
          onClick={() => void callManager.accept(call, true)}
          testId="accept-video"
        >
          <VideoIcon />
        </RoundButton>
      </div>
    </div>
  );
}

// ---- the call window ----------------------------------------------------------------------------------------------

function useTicking(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

function CallWindow() {
  const { t } = useTranslation('messenger');
  const me = useCurrentUser();
  const {
    call,
    status,
    peers,
    minimized,
    audioEnabled,
    videoEnabled,
    screenSharing,
    localStream,
    localVersion,
  } = useCallStore(
    useShallow((s) => ({
      call: s.call,
      status: s.status,
      peers: s.peers,
      minimized: s.minimized,
      audioEnabled: s.audioEnabled,
      videoEnabled: s.videoEnabled,
      screenSharing: s.screenSharing,
      localStream: s.localStream,
      localVersion: s.localVersion,
    })),
  );
  const setMinimized = useCallStore((s) => s.setMinimized);
  const drag = useDraggable();
  const connectedAt = call?.connectedAt ? new Date(call.connectedAt).getTime() : null;
  const now = useTicking(!!connectedAt);
  const duration = connectedAt ? formatDuration((now - connectedAt) / 1000) : null;
  const remote = Object.values(peers);
  const ringing = status === 'active' && !connectedAt && remote.length === 0;
  const others = call?.participants.filter((p) => p.user.id !== me.id) ?? [];
  // A direct call is named after the other person: in the call, or still being rung.
  const counterpart = remote[0]?.user ?? others[0]?.user ?? call?.invited.find((user) => user.id !== me.id);
  const title = call?.chatType === 'group' ? call.chatTitle : (counterpart?.fullName ?? '');

  // The tile of this tab shows the camera or the shared screen.
  const preview = useMemo(() => {
    const track = callManager.previewTrack;
    return track ? new MediaStream([track]) : null;
    // localVersion changes whenever the camera or the screen track changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localVersion, localStream]);

  const controls = (
    <div className="flex items-center justify-center gap-2.5">
      <RoundButton
        label={audioEnabled ? t('call.mute') : t('call.unmute')}
        tone={audioEnabled ? 'neutral' : 'off'}
        onClick={() => callManager.toggleAudio()}
        testId="toggle-mic"
        pressed={!audioEnabled}
      >
        {audioEnabled ? <MicIcon /> : <MicOffIcon />}
      </RoundButton>
      <RoundButton
        label={videoEnabled ? t('call.cameraOff') : t('call.cameraOn')}
        tone={videoEnabled ? 'neutral' : 'off'}
        onClick={() => void callManager.toggleVideo()}
        testId="toggle-camera"
        pressed={!videoEnabled}
      >
        {videoEnabled ? <VideoIcon /> : <VideoOffIcon />}
      </RoundButton>
      {canShareScreen() && !minimized && (
        <RoundButton
          label={screenSharing ? t('call.stopSharing') : t('call.shareScreen')}
          tone={screenSharing ? 'active' : 'neutral'}
          onClick={() => void callManager.toggleScreen()}
          testId="toggle-screen"
          pressed={screenSharing}
          className="hidden sm:inline-flex"
        >
          <MonitorUpIcon />
        </RoundButton>
      )}
      <RoundButton
        label={ringing ? t('call.cancel') : t('call.hangup')}
        tone="danger"
        onClick={() => void callManager.hangup()}
        testId="hangup"
      >
        <PhoneOffIcon />
      </RoundButton>
    </div>
  );

  if (!call) {
    return (
      <div
        className={cn(
          'dark fixed right-5 bottom-5 rounded-2xl border bg-popover px-4 py-3 text-sm text-foreground shadow-2xl',
          layerClass.modal,
        )}
        role="status"
      >
        {t('call.connecting')}
      </div>
    );
  }

  if (minimized) {
    return (
      <div
        role="region"
        aria-label={t('call.title')}
        // Above the composer by default (its send button stays reachable); can be dragged anywhere on the screen.
        className={cn(
          'dark fixed right-3 bottom-24 flex w-[min(22rem,calc(100vw-1.5rem))] cursor-grab touch-none flex-col gap-3 rounded-2xl border bg-popover/95 p-3 text-foreground shadow-2xl backdrop-blur select-none active:cursor-grabbing sm:right-5',
          layerClass.modal,
        )}
        data-testid="call-window"
        data-minimized="true"
        {...drag}
      >
        <div className="flex items-center gap-3">
          <div className="flex -space-x-2">
            {remote.slice(0, 3).map((peer) => (
              <Avatar
                key={peer.connectionId}
                user={peer.user}
                size="md"
                className="rounded-full ring-2 ring-popover"
              />
            ))}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground tabular-nums" data-testid="call-duration">
              {duration ?? t('call.ringing')}
            </p>
          </div>
          <SimpleTooltip content={t('call.expand')}>
            <button
              type="button"
              onClick={() => setMinimized(false)}
              aria-label={t('call.expand')}
              className={iconButton}
            >
              <Maximize2Icon className="size-4" />
            </button>
          </SimpleTooltip>
        </div>
        {controls}
        <HiddenAudio peers={remote} />
      </div>
    );
  }

  const tiles = remote.length + 1;
  const selfTile = (
    <Tile
      name={t('call.you')}
      user={{ id: me.id, fullName: me.fullName, avatarUrl: me.avatarUrl }}
      stream={preview}
      version={localVersion}
      video={videoEnabled || screenSharing}
      audio={audioEnabled}
      screen={screenSharing}
      local
      state="connected"
      compact={tiles === 2}
    />
  );
  return (
    <div
      role="region"
      aria-label={t('call.title')}
      className={cn('dark fixed inset-0 flex flex-col bg-surface text-foreground', layerClass.modal)}
      data-testid="call-window"
      data-call-id={call.id}
      data-connected={connectedAt ? 'true' : 'false'}
    >
      <header className="flex items-center gap-3 px-4 py-3 sm:px-6">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold sm:text-base">{title}</p>
          <p className="text-xs text-muted-foreground tabular-nums" data-testid="call-duration">
            {duration ?? (ringing ? t('call.ringing') : t('call.connecting'))}
          </p>
        </div>
        <SimpleTooltip content={t('call.minimize')}>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            aria-label={t('call.minimize')}
            className={iconButton}
            data-testid="minimize-call"
          >
            <Minimize2Icon className="size-4" />
          </button>
        </SimpleTooltip>
      </header>

      <div className="min-h-0 flex-1 px-3 sm:px-6">
        {ringing ? (
          <div className="flex h-full flex-col items-center justify-center gap-5">
            <span className="relative flex">
              <span className="absolute -inset-3 animate-ping rounded-full bg-foreground/10" aria-hidden />
              {call.chatType === 'direct' && counterpart ? (
                <Avatar user={counterpart} size="2xl" className="relative" />
              ) : (
                <Avatar name={title} seed={call.chatId} size="2xl" shape="rounded" className="relative" />
              )}
            </span>
            <p className="text-sm text-muted-foreground">{t('call.calling', { title })}</p>
          </div>
        ) : tiles === 2 && remote[0] ? (
          // Two people: the other person fills the window, the own picture is a small movable corner view.
          <div className="relative grid h-full grid-cols-1" data-layout="pip">
            <Tile
              name={remote[0].user.fullName}
              user={remote[0].user}
              stream={remote[0].stream}
              version={remote[0].streamVersion}
              video={remote[0].video || remote[0].screen}
              audio={remote[0].audio}
              screen={remote[0].screen}
              state={remote[0].state}
            />
            <SelfView>{selfTile}</SelfView>
          </div>
        ) : (
          <div
            className={cn(
              'grid h-full gap-2 sm:gap-3',
              tiles === 1 && 'grid-cols-1',
              tiles >= 3 && tiles <= 4 && 'grid-cols-2 grid-rows-2',
              tiles >= 5 && 'grid-cols-2 grid-rows-3 md:grid-cols-3 md:grid-rows-2',
            )}
          >
            {selfTile}
            {remote.map((peer) => (
              <Tile
                key={peer.connectionId}
                name={peer.user.fullName}
                user={peer.user}
                stream={peer.stream}
                version={peer.streamVersion}
                video={peer.video || peer.screen}
                audio={peer.audio}
                screen={peer.screen}
                state={peer.state}
              />
            ))}
          </div>
        )}
      </div>

      <footer className="px-4 pt-4 pb-6">{controls}</footer>
    </div>
  );
}

/** The own picture in a two-person call: a corner view over the other person's, dragged anywhere on the screen. */
function SelfView({ children }: { children: ReactNode }) {
  const drag = useDraggable();
  return (
    <div
      className="absolute right-3 bottom-3 z-10 flex aspect-[3/4] w-24 cursor-grab touch-none overflow-hidden rounded-xl shadow-2xl ring-1 ring-border select-none active:cursor-grabbing sm:aspect-video sm:w-56"
      data-testid="call-self-view"
      {...drag}
    >
      {children}
    </div>
  );
}

/** Margin (px) the dragged card keeps from the edges of the window. */
const DRAG_MARGIN = 8;

/**
 * Pointer dragging for the floating call card (buttons inside keep working): a translation from the card's default
 * place, kept inside the window.
 */
function useDraggable() {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const start = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null);
  return {
    style: { transform: `translate(${offset.x}px, ${offset.y}px)` },
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || (event.target as HTMLElement).closest('button')) return;
      start.current = { pointerX: event.clientX, pointerY: event.clientY, x: offset.x, y: offset.y };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      const origin = start.current;
      if (!origin) return;
      // The card's box without the current translation, then the allowed translation range.
      const rect = event.currentTarget.getBoundingClientRect();
      const left = rect.left - offset.x;
      const top = rect.top - offset.y;
      const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
      setOffset({
        x: clamp(
          origin.x + event.clientX - origin.pointerX,
          DRAG_MARGIN - left,
          window.innerWidth - DRAG_MARGIN - (left + rect.width),
        ),
        y: clamp(
          origin.y + event.clientY - origin.pointerY,
          DRAG_MARGIN - top,
          window.innerHeight - DRAG_MARGIN - (top + rect.height),
        ),
      });
    },
    onPointerUp: () => {
      start.current = null;
    },
    onPointerCancel: () => {
      start.current = null;
    },
  };
}

const iconButton =
  'inline-flex size-9 items-center justify-center rounded-full text-muted-foreground focus-ring transition-colors hover:bg-accent hover:text-foreground';

function Tile({
  name,
  user,
  stream,
  version,
  video,
  audio,
  screen,
  local = false,
  state,
  compact = false,
}: {
  name: string;
  user: { id: string; fullName: string; avatarUrl: string | null };
  stream: MediaStream | null;
  version: number;
  video: boolean;
  audio: boolean;
  screen: boolean;
  local?: boolean;
  state: RemotePeer['state'];
  /** The small corner view: a smaller avatar and name badge. */
  compact?: boolean;
}) {
  const { t } = useTranslation('messenger');
  const connecting = !local && state !== 'connected';
  return (
    <div
      className={cn(
        'relative flex min-h-0 items-center justify-center overflow-hidden bg-card',
        compact ? 'size-full rounded-xl' : 'rounded-2xl',
      )}
      data-testid="call-tile"
      data-user-id={user.id}
      data-state={state}
      data-local={local || undefined}
    >
      <StreamVideo
        stream={stream}
        version={version}
        muted={local}
        className={cn(
          'absolute inset-0 size-full',
          screen ? 'object-contain' : 'object-cover',
          local && !screen && '-scale-x-100',
          !video && 'opacity-0',
        )}
      />
      {!video && <Avatar user={user} size={compact ? 'lg' : '2xl'} />}
      <div
        className={cn(
          'absolute flex max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-full bg-overlay text-xs text-foreground backdrop-blur',
          compact ? 'bottom-1.5 left-1.5 px-2 py-0.5' : 'bottom-2 left-2 px-2.5 py-1',
        )}
      >
        {!audio && (
          <MicOffIcon className="size-3.5 shrink-0 text-destructive-text" aria-label={t('call.muted')} />
        )}
        {screen && <MonitorUpIcon className="size-3.5 shrink-0" aria-label={t('call.sharing')} />}
        <span className="truncate">{name}</span>
      </div>
      {connecting && (
        <div className="absolute top-2 right-2 rounded-full bg-overlay px-2.5 py-1 text-xs text-muted-foreground backdrop-blur">
          {state === 'failed' || state === 'disconnected' ? t('call.reconnecting') : t('call.connecting')}
        </div>
      )}
    </div>
  );
}

function StreamVideo({
  stream,
  version,
  muted,
  className,
}: {
  stream: MediaStream | null;
  version: number;
  muted: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.srcObject !== stream) element.srcObject = stream;
    if (stream) void element.play().catch(() => undefined);
  }, [stream, version]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
}

/** Keeps the remote audio playing while the window is minimized. */
function HiddenAudio({ peers }: { peers: RemotePeer[] }) {
  return (
    <div className="hidden">
      {peers.map((peer) => (
        <StreamVideo
          key={peer.connectionId}
          stream={peer.stream}
          version={peer.streamVersion}
          muted={false}
        />
      ))}
    </div>
  );
}

function RoundButton({
  label,
  tone,
  onClick,
  children,
  testId,
  pressed,
  className,
}: {
  label: string;
  tone: 'neutral' | 'off' | 'active' | 'danger' | 'success';
  onClick: () => void;
  children: ReactNode;
  testId?: string;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <SimpleTooltip content={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={pressed}
        data-testid={testId}
        className={cn(
          'inline-flex size-12 items-center justify-center rounded-full focus-ring transition-[background-color,transform] active:scale-95 [&_svg]:size-5',
          tone === 'neutral' && 'bg-secondary text-secondary-foreground hover:bg-accent',
          tone === 'off' && 'bg-foreground text-background hover:bg-foreground/90',
          tone === 'active' && 'bg-info text-brand-foreground hover:bg-info/90',
          tone === 'danger' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
          tone === 'success' && 'bg-success text-brand-foreground hover:bg-success/90',
          className,
        )}
      >
        {children}
      </button>
    </SimpleTooltip>
  );
}
